/**
 * A intencao de contratar, atravessando o login.
 *
 * O problema que isto resolve: quem clica em "Contratar" na lamina
 * publica ainda nao tem sessao, e o login e uma tela que DESCARTA o
 * contexto -- depois de autenticar, o router leva a pessoa para o modulo
 * do perfil dela e a carteira que ela estava vendo se perde. Ela teria de
 * achar o link de novo.
 *
 * POR QUE `sessionStorage` E NAO A URL: o caminho passa pelo Supabase
 * Auth, que redireciona para fora e de volta, e um `?next=` sobreviveria
 * so ate o primeiro salto. Tambem nao vai em `localStorage`: a intencao
 * morre com a aba de proposito -- reabrir o navegador uma semana depois e
 * cair numa tela de confirmacao de compra seria assustador.
 *
 * O PRAZO existe pelo mesmo motivo. Uma aba esquecida aberta a tarde
 * inteira nao deve levar a uma contratacao quando alguem voltar a ela.
 */

const CHAVE = 'contratar';

/** Meia hora: tempo de sobra para criar conta e confirmar o email, curto
 *  o bastante para nao ressuscitar uma decisao antiga. */
const VALIDADE_MS = 30 * 60_000;

export interface IntencaoContratar {
  gestor: string;
  slug: string;
  nome: string;
  /** Epoch ms de quando a pessoa clicou. */
  em: number;
}

/** Guarda a intencao antes de mandar para o login. */
export function guardarIntencao(i: Omit<IntencaoContratar, 'em'>): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify({ ...i, em: Date.now() }));
  } catch {
    // Navegacao anonima com storage bloqueado: a intencao se perde, mas
    // o login continua funcionando. Nao vale barrar por isso.
  }
}

/**
 * Le a intencao sem consumi-la.
 *
 * Devolve null quando nao ha, quando expirou ou quando o conteudo nao
 * confere -- storage e dado que o usuario pode editar, entao nada aqui
 * confia no formato sem olhar.
 */
export function lerIntencao(): IntencaoContratar | null {
  let cru: string | null = null;
  try {
    cru = sessionStorage.getItem(CHAVE);
  } catch {
    return null;
  }
  if (!cru) return null;

  try {
    const d = JSON.parse(cru) as Partial<IntencaoContratar>;
    if (typeof d.gestor !== 'string' || !d.gestor) return null;
    if (typeof d.slug !== 'string' || !d.slug) return null;
    if (typeof d.em !== 'number') return null;
    if (Date.now() - d.em > VALIDADE_MS) {
      limparIntencao();
      return null;
    }
    return {
      gestor: d.gestor, slug: d.slug,
      nome: typeof d.nome === 'string' ? d.nome : '',
      em: d.em,
    };
  } catch {
    // JSON corrompido: some com ele, senao fica barrando para sempre.
    limparIntencao();
    return null;
  }
}

/** Descarta a intencao. Chamado depois de ela ser cumprida ou recusada. */
export function limparIntencao(): void {
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    // Mesmo caso do guardar: sem storage nao ha o que limpar.
  }
}

/** Para onde levar quem tem uma intencao pendente. */
export function rotaDaIntencao(i: IntencaoContratar): string {
  return `/marketplace/contratar/${encodeURIComponent(i.gestor)}`
       + `/${encodeURIComponent(i.slug)}`;
}
